/* global BigInt */
// Buy page. Clerk-gated (any signed-in user) — gate lives in App.js.
// Wallet is still the on-chain identity; Clerk auth lets us log
// (clerkId, wallet, txHash) to the backend for audit after each buy.

import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  useAccount,
  useChainId,
  useReadContracts,
  useWriteContract,
  useSwitchChain,
  useWaitForTransactionReceipt,
  useBalance,
  usePublicClient,
  useWalletClient,
} from 'wagmi';
import { erc20Abi, parseUnits, formatUnits, parseAbiItem } from 'viem';
import { UserButton } from '@clerk/clerk-react';
import { useUnifiedWallet } from '../contexts/UnifiedWalletContext';
import { chainConfig, BSC_CHAIN_ID, USDT_ADDRESS, addressUrl, txUrl } from '../config/chains';
import { useActiveContracts } from '../hooks/useActiveContracts';
import { apiClient } from '../services/apiService';
import './PresaleDashboard.css';

const SITE_NAME = process.env.REACT_APP_SITE_NAME || 'Floobiz';
const TOKEN_SYMBOL = process.env.REACT_APP_TOKEN_SYMBOL || 'FBIZ';

const ONE = 10n ** 18n;
const SCALE = 10n ** 36n;
// On-chain rate is "tokens per 1 USDT" (1e18-scaled). UI shows price
// (USDT per 1 token): price = 1e36 / rate.
function rateToPriceWei(rate) {
  if (!rate || rate === 0n) return 0n;
  return SCALE / rate;
}

// Bought(address indexed buyer, uint256 usdtIn, uint256 tokensOut) — emitted by Sale.buy().
// Used to reconstruct a user's purchase history from chain logs (no backend needed).
const BOUGHT_EVENT = parseAbiItem(
  'event Bought(address indexed buyer, uint256 usdtIn, uint256 tokensOut)'
);

// BSC public RPCs typically cap eth_getLogs at ~5000 blocks per call; stay under that.
const LOG_SCAN_STEP = 4500n;

function purchaseCacheKey(chainId, sale, buyer) {
  return `floobiz:purchases:${chainId}:${sale.toLowerCase()}:${buyer.toLowerCase()}`;
}

// Fetches Bought logs for `buyer` from the Sale contract, incrementally extending a
// localStorage cache so repeat visits only scan new blocks. `refreshKey` re-runs the
// scan when it changes (e.g. after a successful buy).
function usePurchaseHistory({ publicClient, saleAddress, buyer, chainId, refreshKey }) {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!publicClient || !saleAddress || !buyer) {
      setPurchases([]);
      return;
    }
    let cancelled = false;
    const key = purchaseCacheKey(chainId, saleAddress, buyer);
    const deployBlockEnv = Number(process.env.REACT_APP_SALE_DEPLOY_BLOCK || 0) || null;

    (async () => {
      setLoading(true);
      try {
        const cached = (() => {
          try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
        })();
        const existing = cached?.purchases || [];

        const latest = await publicClient.getBlockNumber();
        let fromBlock;
        if (cached?.lastScannedBlock) {
          fromBlock = BigInt(cached.lastScannedBlock) + 1n;
        } else if (deployBlockEnv) {
          fromBlock = BigInt(deployBlockEnv);
        } else {
          fromBlock = latest > LOG_SCAN_STEP ? latest - LOG_SCAN_STEP : 0n;
        }
        if (fromBlock > latest) {
          if (!cancelled) setPurchases(existing);
          return;
        }

        const newLogs = [];
        for (let start = fromBlock; start <= latest; start += LOG_SCAN_STEP + 1n) {
          const end = start + LOG_SCAN_STEP > latest ? latest : start + LOG_SCAN_STEP;
          const logs = await publicClient.getLogs({
            address: saleAddress,
            event: BOUGHT_EVENT,
            args: { buyer },
            fromBlock: start,
            toBlock: end,
          });
          newLogs.push(...logs);
        }

        const timestamps = new Map();
        for (const log of newLogs) {
          if (!timestamps.has(log.blockNumber)) {
            const block = await publicClient.getBlock({ blockNumber: log.blockNumber });
            timestamps.set(log.blockNumber, Number(block.timestamp));
          }
        }

        const formatted = newLogs.map((l) => ({
          txHash: l.transactionHash,
          blockNumber: Number(l.blockNumber),
          timestamp: timestamps.get(l.blockNumber),
          usdtIn: l.args.usdtIn.toString(),
          tokensOut: l.args.tokensOut.toString(),
        }));

        const seen = new Set();
        const merged = [...formatted, ...existing]
          .filter((p) => {
            if (seen.has(p.txHash)) return false;
            seen.add(p.txHash);
            return true;
          })
          .sort((a, b) => b.blockNumber - a.blockNumber);

        if (!cancelled) {
          setPurchases(merged);
          localStorage.setItem(
            key,
            JSON.stringify({ purchases: merged, lastScannedBlock: Number(latest) })
          );
        }
      } catch (err) {
        console.error('purchase history fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [publicClient, saleAddress, buyer, chainId, refreshKey]);

  return { purchases, loading };
}

const SALE_ABI = [
  { type: 'function', name: 'rate', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'saleAllocation', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'sold', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  {
    type: 'function',
    name: 'quote',
    stateMutability: 'view',
    inputs: [{ name: 'usdtAmount', type: 'uint256' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'buy',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'usdtAmount', type: 'uint256' }],
    outputs: [],
  },
];

function PresaleDashboard() {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { connectWallet, formatAddress, disconnectWallet } = useUnifiedWallet();
  const { tokenAddress: TOKEN_ADDRESS, saleAddress: SALE_ADDRESS, loading: contractsLoading } = useActiveContracts();

  const wrongChain = isConnected && chainId !== BSC_CHAIN_ID;
  const bsc = chainConfig('bsc');
  const configured = !!(SALE_ADDRESS && TOKEN_ADDRESS && USDT_ADDRESS);

  const [tokenInput, setTokenInput] = useState('');
  const tokensWei = useMemo(() => {
    try { return parseUnits(String(tokenInput || '0'), 18); } catch { return 0n; }
  }, [tokenInput]);

  // Sale state + user balances
  const reads = useReadContracts({
    contracts: configured
      ? [
          { address: SALE_ADDRESS, abi: SALE_ABI, functionName: 'rate' },
          { address: SALE_ADDRESS, abi: SALE_ABI, functionName: 'saleAllocation' },
          { address: SALE_ADDRESS, abi: SALE_ABI, functionName: 'sold' },
          { address: TOKEN_ADDRESS, abi: erc20Abi, functionName: 'balanceOf', args: [SALE_ADDRESS] },
          ...(address
            ? [
                { address: USDT_ADDRESS, abi: erc20Abi, functionName: 'balanceOf', args: [address] },
                { address: USDT_ADDRESS, abi: erc20Abi, functionName: 'allowance', args: [address, SALE_ADDRESS] },
                { address: TOKEN_ADDRESS, abi: erc20Abi, functionName: 'balanceOf', args: [address] },
              ]
            : []),
        ]
      : [],
    query: { enabled: configured, refetchInterval: 12000 },
  });

  const [rateRaw, allocRaw, soldRaw, saleTokenBal, usdtBal, allowance, tokenBal] =
    reads.data?.map((r) => r.result) ?? [];

  const remaining = allocRaw !== undefined && soldRaw !== undefined ? allocRaw - soldRaw : undefined;
  const priceWei = rateRaw !== undefined ? rateToPriceWei(rateRaw) : 0n;
  const usdtWei = useMemo(() => {
    if (!priceWei || tokensWei === 0n) return 0n;
    return (tokensWei * priceWei) / ONE;
  }, [tokensWei, priceWei]);
  const needsApproval = allowance !== undefined && usdtWei > 0n && allowance < usdtWei;

  // Pre-flight: catch reverts before the wallet prompt so users see a real reason
  // instead of viem's wrapped "gas limit too high" message.
  const buyBlocker = useMemo(() => {
    if (!configured) return null;
    if (rateRaw === 0n) return 'Sale price is not set yet. Please check back shortly.';
    if (allocRaw === 0n) return 'Sale allocation is not configured yet. Please check back shortly.';
    if (tokensWei === 0n) return null; // user hasn't entered an amount; don't nag
    if (remaining !== undefined && tokensWei > remaining) {
      return `Only ${formatUnits(remaining, 18)} ${TOKEN_SYMBOL} remaining in this sale.`;
    }
    if (saleTokenBal !== undefined && tokensWei > saleTokenBal) {
      return `Sale contract is not yet funded with enough ${TOKEN_SYMBOL}. Please check back shortly.`;
    }
    if (usdtBal !== undefined && usdtWei > usdtBal) {
      return `Insufficient USDT: need ${formatUnits(usdtWei, 18)}, have ${formatUnits(usdtBal, 18)}.`;
    }
    return null;
  }, [configured, rateRaw, allocRaw, tokensWei, remaining, saleTokenBal, usdtBal, usdtWei]);

  // Map common wagmi/viem errors to friendlier text.
  function friendlyError(err) {
    if (!err) return null;
    const raw = err.shortMessage || err.message || '';
    if (/User (rejected|denied)/i.test(raw)) return 'Transaction rejected in wallet.';
    if (/gas limit too high/i.test(raw) || /exceeds.*gas/i.test(raw)) {
      return 'The sale rejected this purchase. ' +
        (buyBlocker || 'It may be sold out, underfunded, or not fully configured yet.');
    }
    if (/insufficient funds/i.test(raw)) return 'Not enough BNB to pay gas for this transaction.';
    if (/SoldOut/i.test(raw)) return 'This purchase exceeds the remaining sale allocation.';
    if (/ZeroAmount/i.test(raw)) return 'Amount must be greater than zero.';
    if (/transferFrom failed/i.test(raw)) return 'USDT transfer failed — check your USDT balance and approval.';
    return raw;
  }

  // Approve
  const approveWrite = useWriteContract();
  const [approveHash, setApproveHash] = useState();
  const approveReceipt = useWaitForTransactionReceipt({ hash: approveHash });
  // While an approval tx is mining the wallet has already signed, so
  // approveWrite.isPending is false — but the on-chain allowance hasn't updated
  // yet either. Without this gate the Approve button becomes clickable again
  // between sign and mine, and a second click pops a second MetaMask prompt.
  const approveMining = !!approveHash && !approveReceipt.isSuccess && !approveReceipt.isError;

  // As soon as the approval confirms, force a re-read of allowance so the UI
  // doesn't sit on stale state until the next 12s poll tick.
  useEffect(() => {
    if (approveReceipt.isSuccess) reads.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approveReceipt.isSuccess]);

  // Buy
  const buyWrite = useWriteContract();
  const [buyHash, setBuyHash] = useState();
  // Snapshot the amounts at the moment buy() was signed, so the audit-log
  // POST below has authoritative numbers (input amount = on-chain amount,
  // since Sale.buy uses a fixed rate and exact inputs).
  const [buyMeta, setBuyMeta] = useState(null);
  const buyReceipt = useWaitForTransactionReceipt({ hash: buyHash });
  const buyMining = !!buyHash && !buyReceipt.isSuccess && !buyReceipt.isError;

  const nativeBal = useBalance({ address, query: { enabled: !!address } });

  // Purchase history + add-to-wallet
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { purchases, loading: loadingHistory } = usePurchaseHistory({
    publicClient,
    saleAddress: configured ? SALE_ADDRESS : null,
    buyer: address,
    chainId,
    refreshKey: buyReceipt.isSuccess ? buyHash : null,
  });

  // null = idle, 'pending' = request in flight, 'added' = succeeded,
  // 'unsupported' = wallet rejected the method OR never responded (most
  // mobile wallets over WalletConnect don't expose wallet_watchAsset and
  // simply hang instead of returning an error — hence the timeout). User
  // rejections (code 4001) stay idle — no UI noise.
  const [addTokenStatus, setAddTokenStatus] = useState(null);
  const handleAddToken = async ({ silent = false } = {}) => {
    if (!walletClient || !TOKEN_ADDRESS) return;
    if (!silent) setAddTokenStatus('pending');
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('watchAsset timeout')), 8000),
      );
      const ok = await Promise.race([
        walletClient.watchAsset({
          type: 'ERC20',
          options: { address: TOKEN_ADDRESS, symbol: TOKEN_SYMBOL, decimals: 18 },
        }),
        timeout,
      ]);
      if (!silent) setAddTokenStatus(ok === false ? 'unsupported' : 'added');
    } catch (err) {
      console.debug('watchAsset failed', err);
      if (silent) return;
      const code = err?.code ?? err?.cause?.code;
      const msg = String(err?.message || '').toLowerCase();
      const userRejected = code === 4001 || msg.includes('user rejected') || msg.includes('user denied');
      if (userRejected) {
        setAddTokenStatus(null);
        return;
      }
      setAddTokenStatus('unsupported');
    }
  };

  // Auto-prompt the user to add the token after their FIRST ever buy on this
  // (wallet, token) pair. Persisted in localStorage — a session-only ref would
  // re-prompt on every page reload, which spams users who buy more than once.
  // Manual re-prompt is still available via the "Add to wallet" button.
  const autoAddKey =
    address && TOKEN_ADDRESS
      ? `floobiz:autoAdded:${chainId}:${TOKEN_ADDRESS.toLowerCase()}:${address.toLowerCase()}`
      : null;
  useEffect(() => {
    if (!buyReceipt.isSuccess || !walletClient || !autoAddKey) return;
    if (localStorage.getItem(autoAddKey)) return;
    localStorage.setItem(autoAddKey, '1');
    handleAddToken({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyReceipt.isSuccess, walletClient, autoAddKey]);

  const soldPct = useMemo(() => {
    if (!allocRaw || allocRaw === 0n || soldRaw === undefined) return 0;
    const pct = Number((soldRaw * 10000n) / allocRaw) / 100;
    return Math.min(100, Math.max(0, pct));
  }, [soldRaw, allocRaw]);

  const handleApprove = () => {
    approveWrite.writeContract(
      {
        address: USDT_ADDRESS,
        abi: erc20Abi,
        functionName: 'approve',
        args: [SALE_ADDRESS, usdtWei],
      },
      { onSuccess: setApproveHash },
    );
  };

  const handleBuy = () => {
    const snapshot = {
      usdtIn: usdtWei.toString(),
      tokensOut: tokensWei.toString(),
      contractAddress: SALE_ADDRESS,
      walletAddress: address,
    };
    buyWrite.writeContract(
      {
        address: SALE_ADDRESS,
        abi: SALE_ABI,
        functionName: 'buy',
        args: [usdtWei],
      },
      {
        onSuccess: (hash) => {
          setBuyMeta(snapshot);
          setBuyHash(hash);
        },
      },
    );
  };

  // Fire-and-forget audit log to backend after a confirmed buy. Failures must
  // not affect the on-chain UX — the buy itself is the source of truth.
  const loggedHashRef = useRef(null);
  useEffect(() => {
    if (!buyReceipt.isSuccess || !buyHash || !buyMeta) return;
    if (loggedHashRef.current === buyHash) return;
    loggedHashRef.current = buyHash;
    apiClient
      .post('/purchases/log', { ...buyMeta, txHash: buyHash })
      .catch((err) => {
        console.debug('purchase log failed', err?.response?.data || err?.message);
      });
  }, [buyReceipt.isSuccess, buyHash, buyMeta]);

  return (
    <div className="presale-page">
      <header className="presale-header">
        <h1>{SITE_NAME}</h1>
        <div className="presale-account">
          {isConnected ? (
            <>
              <span className="presale-addr">{formatAddress(address)}</span>
              <button className="presale-btn-ghost" onClick={disconnectWallet}>Disconnect</button>
            </>
          ) : (
            <button className="presale-btn" onClick={connectWallet}>Connect wallet</button>
          )}
          <UserButton afterSignOutUrl="/" />
        </div>
      </header>

      {!configured && (
        <div className="presale-warning">
          {contractsLoading
            ? 'Loading sale…'
            : 'The token sale is launching soon. Please check back shortly.'}
        </div>
      )}

      {configured && (
        <>
          <div className="presale-stats">
            <div className="presale-stat">
              <div className="presale-stat-label">Price</div>
              <div className="presale-stat-value">
                {priceWei && priceWei !== 0n ? `${formatUnits(priceWei, 18)} USDT / ${TOKEN_SYMBOL}` : '—'}
              </div>
            </div>
            <div className="presale-stat">
              <div className="presale-stat-label">Sold</div>
              <div className="presale-stat-value">
                {soldRaw !== undefined && allocRaw !== undefined
                  ? `${formatUnits(soldRaw, 18)} / ${formatUnits(allocRaw, 18)} ${TOKEN_SYMBOL}`
                  : '—'}
              </div>
            </div>
            <div className="presale-stat">
              <div className="presale-stat-label">Remaining</div>
              <div className="presale-stat-value">
                {remaining !== undefined ? `${formatUnits(remaining, 18)} ${TOKEN_SYMBOL}` : '—'}
              </div>
            </div>
          </div>

          {allocRaw !== undefined && allocRaw !== 0n && (
            <div className="presale-progress" aria-label="Sale progress">
              <div className="presale-progress-bar">
                <div className="presale-progress-fill" style={{ width: `${soldPct}%` }} />
              </div>
              <div className="presale-progress-label">
                {soldPct.toFixed(2)}% sold
              </div>
            </div>
          )}

          <section className="presale-card">
            <h2>Buy {TOKEN_SYMBOL}</h2>

            {!isConnected ? (
              <button className="presale-btn" onClick={connectWallet}>Connect wallet to buy</button>
            ) : wrongChain ? (
              <div className="presale-warning">
                Wrong network ({chainId}).{' '}
                <button onClick={() => switchChain({ chainId: BSC_CHAIN_ID })}>Switch to {bsc.name}</button>
              </div>
            ) : (
              <>
                <label className="presale-input-row">
                  {TOKEN_SYMBOL} amount
                  <input
                    inputMode="decimal"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    placeholder="e.g. 1000"
                  />
                </label>

                <div className="presale-quote">
                  You pay: <strong>{formatUnits(usdtWei, 18)} USDT</strong>
                </div>

                <div className="presale-balances">
                  <div>USDT balance: {usdtBal !== undefined ? formatUnits(usdtBal, 18) : '—'}</div>
                  <div>{TOKEN_SYMBOL} balance: {tokenBal !== undefined ? formatUnits(tokenBal, 18) : '—'}</div>
                  <div>{bsc.label} balance: {nativeBal.data ? formatUnits(nativeBal.data.value, 18) : '—'}</div>
                </div>

                <div className="presale-actions">
                  <button
                    className="presale-btn"
                    onClick={handleApprove}
                    disabled={!needsApproval || approveWrite.isPending || approveMining || usdtWei === 0n}
                  >
                    {!needsApproval
                      ? '1. Approve USDT ✓'
                      : approveWrite.isPending
                      ? 'Confirm in wallet…'
                      : approveMining
                      ? 'Approving…'
                      : '1. Approve USDT'}
                  </button>

                  <button
                    className="presale-btn presale-btn-primary"
                    onClick={handleBuy}
                    disabled={needsApproval || buyWrite.isPending || buyMining || usdtWei === 0n || !!buyBlocker}
                  >
                    {buyWrite.isPending
                      ? 'Confirm in wallet…'
                      : buyMining
                      ? 'Buying…'
                      : `2. Buy ${TOKEN_SYMBOL}`}
                  </button>
                </div>

                {buyBlocker && <div className="presale-err">{buyBlocker}</div>}

                {approveHash && (
                  <div className="presale-tx">
                    Approve: <a href={txUrl('bsc', approveHash)} target="_blank" rel="noreferrer">{approveHash.slice(0, 10)}…</a>{' '}
                    {approveReceipt.isSuccess ? '✓' : approveReceipt.isLoading ? '(mining…)' : ''}
                  </div>
                )}
                {buyHash && (
                  <div className="presale-tx">
                    Buy: <a href={txUrl('bsc', buyHash)} target="_blank" rel="noreferrer">{buyHash.slice(0, 10)}…</a>{' '}
                    {buyReceipt.isSuccess ? '✓ confirmed' : buyReceipt.isLoading ? '(mining…)' : ''}
                  </div>
                )}

                {approveWrite.error && <div className="presale-err">{friendlyError(approveWrite.error)}</div>}
                {buyWrite.error && <div className="presale-err">{friendlyError(buyWrite.error)}</div>}
              </>
            )}

            <div className="presale-footnote">
              Sale: <a href={addressUrl('bsc', SALE_ADDRESS)} target="_blank" rel="noreferrer">{SALE_ADDRESS.slice(0,6)}…{SALE_ADDRESS.slice(-4)}</a>{' '}
              · Token: <a href={addressUrl('bsc', TOKEN_ADDRESS)} target="_blank" rel="noreferrer">{TOKEN_ADDRESS.slice(0,6)}…{TOKEN_ADDRESS.slice(-4)}</a>
              <CopyButton value={TOKEN_ADDRESS} label={`Copy ${TOKEN_SYMBOL} address`} />
            </div>
          </section>

          {isConnected && !wrongChain && (
            <section className="presale-card presale-history">
              <div className="presale-history-head">
                <div>
                  <h2>Your {TOKEN_SYMBOL}</h2>
                  <div className="presale-history-balance">
                    {tokenBal !== undefined ? formatUnits(tokenBal, 18) : '—'} {TOKEN_SYMBOL}
                  </div>
                </div>
                <div className="presale-add-token">
                  <button
                    className="presale-btn-ghost"
                    onClick={() => handleAddToken()}
                    disabled={!walletClient || addTokenStatus === 'pending'}
                  >
                    {addTokenStatus === 'pending'
                      ? 'Check your wallet…'
                      : `+ Add ${TOKEN_SYMBOL} to wallet`}
                  </button>
                  <div className="presale-add-token-manual">
                    Or import manually:
                    <code className="presale-add-token-addr">{TOKEN_ADDRESS}</code>
                    <CopyButton value={TOKEN_ADDRESS} label={`Copy ${TOKEN_SYMBOL} address`} />
                  </div>
                  {addTokenStatus === 'added' && (
                    <span className="presale-add-token-ok">Added ✓</span>
                  )}
                  {addTokenStatus === 'unsupported' && (
                    <div className="presale-add-token-fallback">
                      Your wallet didn't respond to the auto-add request (common on
                      mobile). Copy the address above and paste it under "Add custom
                      token" on Smart Chain (decimals: 18).
                    </div>
                  )}
                </div>
              </div>

              <h3 className="presale-history-title">Purchase history</h3>
              {loadingHistory && purchases.length === 0 ? (
                <div className="presale-history-empty">Loading history…</div>
              ) : purchases.length === 0 ? (
                <div className="presale-history-empty">
                  No purchases yet on this wallet. Once you buy, your transactions will show up here.
                </div>
              ) : (
                <ul className="presale-history-list">
                  {purchases.map((p) => (
                    <li key={p.txHash} className="presale-history-row">
                      <div className="presale-history-when">
                        {p.timestamp
                          ? new Date(p.timestamp * 1000).toLocaleString()
                          : `block ${p.blockNumber}`}
                      </div>
                      <div className="presale-history-amounts">
                        <span className="presale-history-tokens">
                          +{Number(formatUnits(BigInt(p.tokensOut), 18)).toLocaleString(undefined, { maximumFractionDigits: 4 })} {TOKEN_SYMBOL}
                        </span>
                        <span className="presale-history-cost">
                          for {Number(formatUnits(BigInt(p.usdtIn), 18)).toLocaleString(undefined, { maximumFractionDigits: 4 })} USDT
                        </span>
                      </div>
                      <a
                        className="presale-history-link"
                        href={txUrl('bsc', p.txHash)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {p.txHash.slice(0, 8)}…
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onClick = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.debug('copy failed', err);
    }
  };
  return (
    <button type="button" className="presale-copy-btn" onClick={onClick} aria-label={label}>
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

export default PresaleDashboard;
